// Inlined from "graphql-ruby-client@1.11.9/subscriptions/ActionCableLink.ts"

import { ApolloLink, Observable } from '@apollo/client';
import type { Consumer } from '@rails/actioncable';
import { print } from 'graphql';

import { IS_TEST_RUNNER } from '#src/config.ts';

type RequestResult = ApolloLink.Result<
  { [key: string]: any },
  Record<string, any>
>;
type ConnectionParams = object | ((operation: ApolloLink.Operation) => object);

export class ActionCableLink extends ApolloLink {
  cable: Consumer;

  channelName: string;

  actionName: string;

  connectionParams: ConnectionParams;

  constructor(options: {
    cable: Consumer;
    channelName?: string;
    actionName?: string;
    connectionParams?: ConnectionParams;
  }) {
    super();
    this.cable = options.cable;
    this.channelName = options.channelName || 'GraphqlChannel';
    this.actionName = options.actionName || 'execute';
    this.connectionParams = options.connectionParams || {};
  }

  // Interestingly, this link does _not_ call through to `next` because
  // instead, it sends the request to ActionCable.
  request(
    operation: ApolloLink.Operation,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _next: ApolloLink.ForwardFunction
  ): Observable<RequestResult> {
    return new Observable(observer => {
      if (IS_TEST_RUNNER) {
        // msw cannot mock the ActionCable protocol (https://github.com/mswjs/msw/issues/285),
        // so subscriptions do nothing unless a story installed the fake cable
        // from tests/mockActionCable.ts to drive payloads by hand.
        return globalThis.mockActionCable?.subscribe(
          {
            operationName: operation.operationName,
            query: operation.query,
            variables: operation.variables,
          },
          observer
        );
      }
      const channelId = Math.round(
        Date.now() + Math.random() * 100000
      ).toString(16);
      const { actionName } = this;
      const connectionParams =
        typeof this.connectionParams === 'function'
          ? this.connectionParams(operation)
          : this.connectionParams;
      const channel = this.cable.subscriptions.create(
        {
          channel: this.channelName,
          channelId,
          ...connectionParams,
        },
        {
          connected() {
            const { operationId } = operation as { operationId?: string };
            this.perform(actionName, {
              // This is added for persisted operation support:
              operationId,
              query:
                operation.query && !operationId ? print(operation.query) : null,
              variables: operation.variables,
              operationName: operation.operationName,
            });
          },
          received(payload) {
            if (payload?.result?.data || payload?.result?.errors) {
              observer.next(payload.result);
            }

            if (!payload.more) {
              observer.complete();
            }
          },
        }
      );
      // Make the ActionCable subscription behave like an Apollo subscription
      return Object.assign(channel, { closed: false });
    });
  }
}
